import { zodResolver } from '@hookform/resolvers/zod';
import { SettingsIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/core/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/core/card';
import { Form } from '@/components/core/form';
import { Separator } from '@/components/core/separator';
import { TextFormField } from '@/components/forms/text-form-field';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page, PageContent } from '@/components/layout';
import { baseUrlProvider } from '@/gateways/baseUrlProvider';
import type { GatewayName } from '@/gateways/types';
import { toTitleCase } from '@/shared/helpers';
import { useMessages } from '@/shared/providers/MessagesProvider';

const ticketFormSchema = z.object({
  branch: z
    .string()
    .min(1, 'Enter a branch')
    .refine((value) => value === value.trim(), {
      message: 'Please remove leading/trailing spaces',
    }),
});

const gatewayNames = [
  'accounts',
  'analytics',
  'automation',
  'integration',
  'inventory',
  'products',
  'sales',
  'shipping',
  'supply',
  'warehousing',
  'graphql',
] as const satisfies GatewayName[];

const urlFormSchema = z
  .record(
    z
      .string()
      .min(1, 'Enter a base URL')
      .refine((value) => value === value.trim(), {
        message: 'Please remove leading/trailing spaces',
      }),
    z.string(),
  )

  .superRefine((value, ctx) => {
    gatewayNames.forEach((name) => {
      if (!value[name]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a base URL',
          path: [name],
        });
      }
    });
  });

export function DeveloperConfig() {
  const messages = useMessages();
  const [isConfiguringUrls, setIsConfiguringUrls] = useState(false);
  const baseUrls = useMemo(() => baseUrlProvider.getForAllGateways(), []);

  const ticketForm = useForm({
    resolver: zodResolver(ticketFormSchema),
    defaultValues: {
      branch: 'eng-',
    },
  });

  const urlForm = useForm({
    resolver: zodResolver(urlFormSchema),
    defaultValues: baseUrls,
  });

  const handleConfigureForTicket = ticketForm.handleSubmit(async ({ branch }) => {
    setIsConfiguringUrls(true);
    baseUrlProvider.reset();

    try {
      const entries = await Promise.all(
        Object.entries(baseUrlProvider.getForAllGateways()).map(async ([name, baseUrl]) => {
          const branchUrl = `${baseUrl}-${branch}`;
          const response = await fetch(`${branchUrl}/v1/info`);
          if (![403, 404].includes(response.status)) {
            return [name, branchUrl] as [GatewayName, string];
          }
          return null;
        }),
      );
      const filteredEntries = entries.filter(
        (entry): entry is [GatewayName, string] => entry !== null,
      );

      if (filteredEntries.length) {
        baseUrlProvider.setForGateways(Object.fromEntries(filteredEntries));
        messages.success(
          `Configured ${filteredEntries.length}/${Object.keys(baseUrls).length} base URLs for ${branch}`,
        );
      } else {
        messages.error(`Unable to find any matching gateways for ${branch}`);
      }

      urlForm.reset(baseUrlProvider.getForAllGateways());
    } finally {
      setIsConfiguringUrls(false);
    }
  });

  const handleUpdateUrls = urlForm.handleSubmit((data) => {
    baseUrlProvider.setForGateways(data);
    messages.success('Updated API base URLs');
  });

  const handleResetUrls = () => {
    baseUrlProvider.reset();
    urlForm.reset(baseUrlProvider.getForAllGateways());
    messages.success('Reset API base URLs');
  };

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <SettingsIcon />
        </HeaderIcon>
        <HeaderTitle>Developer Config</HeaderTitle>
      </Header>
      <PageContent variant="column">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>API Base URLs</CardTitle>
            <CardDescription>
              Configure gateway endpoints for local development and testing
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <Form {...ticketForm}>
              <form
                className="flex flex-col items-start gap-4 sm:flex-row sm:items-end"
                onSubmit={handleConfigureForTicket}
              >
                <TextFormField
                  control={ticketForm.control}
                  name="branch"
                  label="Ticket branch"
                  placeholder="eng-1234"
                  className="w-40"
                  required
                />
                <Button type="submit" loading={isConfiguringUrls}>
                  Configure for ticket
                </Button>
              </form>
            </Form>
            <Separator />
            <Form {...urlForm}>
              <form className="flex flex-col gap-6" onSubmit={handleUpdateUrls}>
                <div className="grid gap-4 md:grid-cols-2">
                  {(Object.keys(baseUrls) as GatewayName[]).map((key) => (
                    <TextFormField
                      key={key}
                      control={urlForm.control}
                      name={key}
                      label={toTitleCase(key)}
                      className="w-full"
                    />
                  ))}
                </div>
                <div className="flex flex-wrap justify-end gap-3">
                  <Button type="button" variant="outline" onClick={handleResetUrls}>
                    Reset
                  </Button>
                  <Button type="submit">Save</Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </PageContent>
    </Page>
  );
}
